import type {
  AttributeFixContext,
  AmazonConnection,
  ActiveRepricingImportItem,
  FeeEstimateEntry,
  FinanceAlertsResponse,
  FinanceEventsResponse,
  FinanceOverview,
  ProfitPreviewResult,
  SkuCostEntry,
  SkuProfitResponse,
  AppSettings,
  AuditEvent,
  BrandLabCapabilities,
  CatalogItem,
  CatalogImageCase,
  CatalogImageCaseOpen,
  CatalogImageCaseOpenResult,
  CatalogImagePreparation,
  CorrectionWorkflow,
  CatalogSearchResponse,
  ContentQualityResponse,
  ContentQualityIssue,
  DashboardData,
  Job,
  ListingDraft,
  ListingDetailResponse,
  ListingMatrixResponse,
  ListingPreview,
  Marketplace,
  MarketplaceCode,
  MediaAsset,
  PagedResponse,
  PlaybookCatalogResponse,
  ProductType,
  ProductTypeDefinitionResponse,
  QualitySeverity,
  Restriction,
  RolloutPreparation,
  SettingsTemplate,
  ShippingOverviewResponse,
  ShippingSettings,
  ShippingTemplate,
  User,
  WorkQueueResponse,
  WorkView,
  ProductReviewKind,
  ProductReviewDetail,
  ProductReviewsResponse,
  ProductReviewProblemsResponse,
  ProductReviewProblemDetail,
  ProblemTitleContext,
  ProblemTitlePreview,
  ProblemTitleCheck,
  ProductReviewThumbnail,
  ProductReviewIdentity,
  ProductReviewSubmit,
  ProductReviewPreview,
  ProductReviewVerification,
  ProductReviewBatchDetail,
  ProductReviewBatchIdentity,
  ProductReviewBatchPreview,
  ProductReviewBatchResult,
  ReviewImage,
} from "./types";

export class ApiError extends Error {
  readonly status: number;
  readonly details?: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

function errorMessage(payload: unknown, fallback: string): string {
  if (typeof payload === "string") return payload;
  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    if (typeof record.detail === "string") return record.detail;
    if (typeof record.message === "string") return record.message;
    if (Array.isArray(record.detail)) {
      return record.detail
        .map((item) => {
          if (typeof item === "string") return item;
          if (item && typeof item === "object" && "msg" in item) {
            return String((item as { msg: unknown }).msg);
          }
          return "";
        })
        .filter(Boolean)
        .join(" · ");
    }
  }
  return fallback;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (!(init.body instanceof FormData) && init.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("Accept", "application/json");

  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      headers,
      credentials: "include",
    });
  } catch (error) {
    throw new ApiError(
      "Der API-Server ist momentan nicht erreichbar. Bitte Verbindung prüfen und erneut versuchen.",
      0,
      error,
    );
  }

  const isJson = response.headers.get("content-type")?.includes("application/json");
  const payload = response.status === 204 ? undefined : isJson ? await response.json() : await response.text();

  if (!response.ok) {
    throw new ApiError(
      errorMessage(payload, `Anfrage fehlgeschlagen (${response.status}).`),
      response.status,
      payload,
    );
  }
  return payload as T;
}

const json = (value: unknown) => JSON.stringify(value);

export const api = {
  productReviews: (params: { kind: ProductReviewKind; status: "due" | "all" | "archived"; marketplace?: string; q?: string; page?: number; pageSize?: number; grouped?: boolean }) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => { if (value !== undefined && value !== "") search.set(key, String(value)); });
    return request<ProductReviewsResponse>(`/api/product-reviews?${search}`);
  },
  productReviewProblems: (params: { marketplace?: string; q?: string; page?: number; pageSize?: number }) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => { if (value !== undefined && value !== "") search.set(key, String(value)); });
    return request<ProductReviewProblemsResponse>(`/api/product-reviews/problems?${search}`);
  },
  productReviewProblemDetail: (sellerSku: string, marketplace?: MarketplaceCode) =>
    request<ProductReviewProblemDetail>(`/api/product-reviews/problems/detail?${new URLSearchParams({ sellerSku, ...(marketplace ? { marketplace } : {}) })}`),
  recheckProductReviewProblem: (sellerSku: string, marketplace: MarketplaceCode) =>
    request<ProductReviewProblemDetail>("/api/product-reviews/problems/recheck", { method: "POST", body: json({ sellerSku, marketplace }) }),
  problemTitleContext: (issueId: string) =>
    request<ProblemTitleContext>(`/api/product-reviews/problems/title/context?${new URLSearchParams({ issueId })}`),
  problemTitlePreview: (payload: { issueId: string; evidenceKey: string; title: string; languageConfirmed: true }) =>
    request<ProblemTitlePreview>("/api/product-reviews/problems/title/preview", { method: "POST", body: json(payload) }),
  problemTitleSubmit: (payload: ProductReviewSubmit) =>
    request<{ jobId: string; status: string }>("/api/product-reviews/problems/title/submit", { method: "POST", body: json(payload) }),
  problemTitleCheck: (payload: { jobId: string }) =>
    request<ProblemTitleCheck>("/api/product-reviews/problems/title/check", { method: "POST", body: json(payload) }),
  productReviewDetail: (sellerSku: string, marketplace: MarketplaceCode) =>
    request<ProductReviewDetail>(`/api/product-reviews/detail?${new URLSearchParams({ sellerSku, marketplace })}`),
  productReviewThumbnail: (sellerSku: string, marketplace: MarketplaceCode, asin: string, refresh = false) =>
    request<ProductReviewThumbnail>(`/api/product-reviews/thumbnail?${new URLSearchParams({ sellerSku, marketplace, asin, refresh: String(refresh) })}`),
  productReviewBatchDetail: (sellerSku: string) =>
    request<ProductReviewBatchDetail>(`/api/product-reviews/batch/detail?${new URLSearchParams({ sellerSku })}`),
  previewProductImagesBatch: (payload: ProductReviewBatchIdentity & { images: ReviewImage[] }) =>
    request<ProductReviewBatchPreview>("/api/product-reviews/images/batch/preview", { method: "POST", body: json(payload) }),
  previewProductSafetyBatch: (payload: ProductReviewBatchIdentity & { attributes: Record<string, unknown> }) =>
    request<ProductReviewBatchPreview>("/api/product-reviews/safety/batch/preview", { method: "POST", body: json(payload) }),
  submitProductReviewBatch: (kind: ProductReviewKind, payload: ProductReviewSubmit) =>
    request<{ jobId: string; status: string; items?: ProductReviewBatchResult["items"] }>(`/api/product-reviews/${kind}/batch/submit`, { method: "POST", body: json(payload) }),
  verifyProductReviewBatch: (sellerSku: string, kind: ProductReviewKind, marketplaces: MarketplaceCode[]) =>
    request<ProductReviewBatchResult>("/api/product-reviews/batch/verify", { method: "POST", body: json({ sellerSku, kind, marketplaces }) }),
  confirmProductReviewBatch: (kind: ProductReviewKind, payload: { sellerSku: string; targets: Array<{ marketplace: MarketplaceCode; expectedRevision: number; reviewToken: string }>; confirmed: true }) =>
    request<ProductReviewBatchResult>(`/api/product-reviews/${kind}/batch/confirm`, { method: "POST", body: json(payload) }),
  restartProductImageReviewBatch: (payload: { sellerSku: string; targets: Array<{ marketplace: MarketplaceCode; expectedRevision: number; reviewToken: string }>; confirmed: true }) =>
    request<ProductReviewBatchResult>("/api/product-reviews/images/batch/restart", { method: "POST", body: json(payload) }),
  saveProductImages: (payload: ProductReviewIdentity & { images: ReviewImage[] }) =>
    request<{ revision: number; images: ProductReviewDetail["images"] }>("/api/product-reviews/images/draft", { method: "PUT", body: json(payload) }),
  previewProductImages: (payload: ProductReviewIdentity) =>
    request<ProductReviewPreview>("/api/product-reviews/images/preview", { method: "POST", body: json(payload) }),
  previewProductSafety: (payload: ProductReviewIdentity & { attributes: Record<string, unknown> }) =>
    request<ProductReviewPreview>("/api/product-reviews/safety/preview", { method: "POST", body: json(payload) }),
  submitProductReview: (kind: ProductReviewKind, payload: ProductReviewSubmit) =>
    request<{ jobId: string; status: string }>(`/api/product-reviews/${kind}/submit`, { method: "POST", body: json(payload) }),
  verifyProductReview: (sellerSku: string, marketplace: MarketplaceCode, kind: ProductReviewKind) =>
    request<ProductReviewVerification>("/api/product-reviews/verify", { method: "POST", body: json({ sellerSku, marketplace, kind }) }),
  confirmProductReview: (kind: ProductReviewKind, payload: ProductReviewIdentity & { reviewToken: string; confirmed: true }) =>
    request<{ revision: number; status: string; reviewedAt: string }>(`/api/product-reviews/${kind}/confirm`, { method: "POST", body: json(payload) }),
  restartProductImageReview: (payload: ProductReviewIdentity & { reviewToken: string; confirmed: true }) =>
    request<{ revision: number; status: string; restartedAt: string }>("/api/product-reviews/images/restart", { method: "POST", body: json(payload) }),
  confirmImageReadback: (payload: ProductReviewIdentity & { reviewToken: string; jobId: string; confirmed: true }) =>
    request<{ revision: number; status: "verified"; verifiedAt: string }>("/api/product-reviews/images/readback/confirm", { method: "POST", body: json(payload) }),
  openCatalogImageCases: (payload: CatalogImageCaseOpen) =>
    request<CatalogImageCaseOpenResult>("/api/product-reviews/images/catalog-cases", { method: "POST", body: json(payload) }),
  prepareCatalogImageCases: (payload: Omit<CatalogImageCaseOpen, "caseReference">) =>
    request<{ items: Array<{ marketplaceCode: MarketplaceCode; status: string; preparation?: CatalogImagePreparation; error?: string }> }>("/api/product-reviews/images/catalog-case-preparations", { method: "POST", body: json(payload) }),
  catalogImagePreparations: (sellerSku: string) =>
    request<{ items: CatalogImagePreparation[] }>(`/api/product-reviews/images/catalog-case-preparations?${new URLSearchParams({ seller_sku: sellerSku })}`),
  openPreparedCatalogImageCase: (id: string, caseReference: string, submissionJobId: string) =>
    request<{ case: CatalogImageCase; preparation: CatalogImagePreparation }>(`/api/product-reviews/images/catalog-case-preparations/${encodeURIComponent(id)}/open`, { method: "POST", body: json({ caseReference, submissionJobId, confirmed: true }) }),
  cancelCatalogImagePreparation: (id: string) =>
    request<CatalogImagePreparation>(`/api/product-reviews/images/catalog-case-preparations/${encodeURIComponent(id)}/cancel`, { method: "POST", body: json({ confirmed: true }) }),
  correctionWorkflows: (sellerSku: string) =>
    request<{ items: CorrectionWorkflow[] }>(`/api/product-reviews/images/correction-workflows?${new URLSearchParams({ seller_sku: sellerSku })}`),
  startCorrectionWorkflows: (payload: { sellerSku: string; targets: Array<{ marketplace: MarketplaceCode; expectedRevision: number; reviewToken: string }>; wrongImageUrls: string[]; correctImageUrls: string[]; reason: string; mode: "online" | "upload"; confirmed: true }) =>
    request<{ items: Array<{ marketplaceCode: MarketplaceCode; status: string; workflow?: CorrectionWorkflow; error?: string }> }>("/api/product-reviews/images/correction-workflows", { method: "POST", body: json(payload) }),
  reviewCorrectionWorkflow: (id: string, payload: { expectedRevision: number; reviewToken: string; correctImageUrls: string[]; confirmed: true }) =>
    request<{ workflow: CorrectionWorkflow }>(`/api/product-reviews/images/correction-workflows/${encodeURIComponent(id)}/review`, { method: "POST", body: json(payload) }),
  openCorrectionWorkflow: (id: string, payload: { expectedRevision: number; caseReference: string; confirmed: true; imageReasons?: Record<string, string> }) =>
    request<{ workflow: CorrectionWorkflow; cases: CatalogImageCase[] }>(`/api/product-reviews/images/correction-workflows/${encodeURIComponent(id)}/open`, { method: "POST", body: json(payload) }),
  completeCorrectionWorkflow: (id: string, payload: { expectedRevision: number; confirmed: true }) =>
    request<{ workflow: CorrectionWorkflow }>(`/api/product-reviews/images/correction-workflows/${encodeURIComponent(id)}/complete`, { method: "POST", body: json(payload) }),
  cancelCorrectionWorkflow: (id: string, payload: { expectedRevision: number; confirmed: true }) =>
    request<{ workflow: CorrectionWorkflow }>(`/api/product-reviews/images/correction-workflows/${encodeURIComponent(id)}/cancel`, { method: "POST", body: json(payload) }),
  checkCatalogImageCase: (id: string, expectedRevision: number) =>
    request<CatalogImageCase>(`/api/product-reviews/images/catalog-cases/${encodeURIComponent(id)}/check`, { method: "POST", body: json({ expectedRevision, confirmed: true }) }),
  withdrawCatalogImageCase: (id: string, expectedRevision: number) =>
    request<CatalogImageCase>(`/api/product-reviews/images/catalog-cases/${encodeURIComponent(id)}/withdraw`, { method: "POST", body: json({ expectedRevision, confirmed: true }) }),
  health: () => request<{ status?: string }>("/health"),
  login: async (email: string, password: string): Promise<User> => {
    const result = await request<User | { user: User }>("/api/auth/login", {
      method: "POST",
      body: json({ email, password }),
    });
    return "user" in result ? result.user : result;
  },
  logout: () => request<void>("/api/auth/logout", { method: "POST" }),
  me: async (): Promise<User> => {
    const result = await request<User | { user: User }>("/api/auth/me");
    return "user" in result ? result.user : result;
  },

  dashboard: () => request<DashboardData>("/api/dashboard"),
  tasks: (view: WorkView = "now", offset = 0) =>
    request<WorkQueueResponse>(`/api/tasks?view=${view}&offset=${offset}&limit=20`),
  connection: () => request<AmazonConnection>("/api/amazon/connection"),
  testConnection: () =>
    request<AmazonConnection>("/api/amazon/connection/test", { method: "POST", body: json({}) }),
  marketplaces: async (): Promise<Marketplace[]> => {
    const result = await request<Marketplace[] | { items: Marketplace[] }>("/api/marketplaces");
    return Array.isArray(result) ? result : result.items;
  },
  refreshMarketplaces: async (): Promise<Marketplace[]> => {
    const result = await request<Marketplace[] | { items: Marketplace[] }>("/api/marketplaces/refresh", {
      method: "POST",
      body: json({}),
    });
    return Array.isArray(result) ? result : result.items;
  },

  catalogSearch: (q: string, marketplace: MarketplaceCode = "DE") =>
    request<CatalogSearchResponse>(
      `/api/catalog/search?q=${encodeURIComponent(q)}&marketplace=${marketplace}`,
    ),
  catalogItem: (asin: string, marketplace: MarketplaceCode = "DE") =>
    request<CatalogItem>(
      `/api/catalog/items/${encodeURIComponent(asin)}?marketplace=${marketplace}`,
    ),
  restrictions: async (asin: string): Promise<Restriction[]> => {
    const result = await request<Restriction[] | { items: Restriction[] }>(
      `/api/catalog/items/${encodeURIComponent(asin)}/restrictions`,
    );
    return Array.isArray(result) ? result : result.items;
  },
  productTypes: async (q: string, marketplace: MarketplaceCode = "DE"): Promise<ProductType[]> => {
    const result = await request<ProductType[] | { items: ProductType[] }>(
      `/api/catalog/product-types/search?q=${encodeURIComponent(q)}&marketplace=${marketplace}`,
    );
    return Array.isArray(result) ? result : result.items;
  },
  productType: (productType: string, marketplace: MarketplaceCode = "DE") =>
    request<ProductTypeDefinitionResponse>(
      `/api/catalog/product-types/${encodeURIComponent(productType)}?marketplace=${marketplace}`,
    ),

  matrix: (params: {
    q?: string;
    filter?: string;
    fulfillment?: string;
    status?: string;
    cursor?: string;
  } = {}) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => value && search.set(key, value));
    return request<ListingMatrixResponse>(`/api/listings/matrix${search.size ? `?${search}` : ""}`);
  },
  shippingOverview: (params: {
    marketplace: MarketplaceCode;
    q?: string;
    fulfillment?: string;
    problemsOnly?: boolean;
    limit?: number;
  }) => {
    const search = new URLSearchParams({ marketplace: params.marketplace });
    if (params.q) search.set("q", params.q);
    if (params.fulfillment) search.set("fulfillment", params.fulfillment);
    if (params.problemsOnly) search.set("problemsOnly", "true");
    if (params.limit) search.set("limit", String(params.limit));
    return request<ShippingOverviewResponse>(`/api/listings/shipping-overview?${search}`);
  },
  previewShippingAssignment: (payload: {
    sellerSku: string;
    marketplace: MarketplaceCode;
    shippingTemplateId: string;
  }) =>
    request<ListingPreview>("/api/listings/shipping-assignment/preview", {
      method: "POST",
      body: json(payload),
    }),
  activeRepricingImport: () => request<ActiveRepricingImportItem[]>("/api/imports"),
  listing: (sellerSku: string) =>
    request<ListingDetailResponse>(`/api/listings/${encodeURIComponent(sellerSku)}`),
  previewListing: (draft: ListingDraft) =>
    request<ListingPreview>("/api/listings/preview", { method: "POST", body: json(draft) }),
  previewPriceChange: (payload: {
    sellerSku: string;
    marketplace: MarketplaceCode;
    price: number;
  }) =>
    request<ListingPreview>("/api/listings/price-change/preview", {
      method: "POST",
      body: json(payload),
    }),
  submitListing: (payload: {
    previewId: string;
    confirmationToken: string;
    idempotencyKey: string;
  }) =>
    request<{ jobId: string; status: string }>("/api/listings/submit", {
      method: "POST",
      body: json(payload),
    }),
  prepareRollout: (payload: { sellerSku: string; targetMarketplaces: MarketplaceCode[] }) =>
    request<RolloutPreparation>("/api/listings/rollout/prepare", { method: "POST", body: json(payload) }),
  previewRollout: (payload: {
    sellerSku: string;
    gtin?: string;
    billbeeId?: string;
    identityId?: string;
    skuUnit?: ListingDraft["skuUnit"];
    marketplaceAttributes?: Partial<Record<MarketplaceCode, Record<string, unknown>>>;
    targetMarketplaces: MarketplaceCode[];
    fulfillmentMode: "FBM" | "FBA";
    startPrices: Partial<Record<MarketplaceCode, number>>;
    shippingTemplates: Partial<Record<MarketplaceCode, string>>;
  }) =>
    request<ListingPreview>("/api/listings/rollout/preview", {
      method: "POST",
      body: json(payload),
    }),
  submitRollout: (payload: {
    previewId: string;
    confirmationToken: string;
    idempotencyKey: string;
  }) =>
    request<{ jobId: string; status: string }>("/api/listings/rollout/submit", {
      method: "POST",
      body: json(payload),
    }),
  verifyListing: (sellerSku: string) =>
    request<{ jobId?: string; status?: string }>(
      `/api/listings/${encodeURIComponent(sellerSku)}/verify`,
      { method: "POST", body: json({}) },
    ),

  uploadMedia: (file: File, metadata?: Record<string, string>) => {
    const body = new FormData();
    body.append("file", file);
    Object.entries(metadata ?? {}).forEach(([key, value]) => body.append(key, value));
    return request<MediaAsset>("/api/media", { method: "POST", body });
  },
  media: (assetId: string) => request<MediaAsset>(`/api/media/${encodeURIComponent(assetId)}`),
  transformMedia: (assetId: string, transform: Record<string, unknown>) =>
    request<MediaAsset>(`/api/media/${encodeURIComponent(assetId)}/transform`, {
      method: "POST",
      body: json(transform),
    }),
  deleteMedia: (assetId: string) =>
    request<void>(`/api/media/${encodeURIComponent(assetId)}`, { method: "DELETE" }),

  contentQualityIssues: (
    params: {
      seller_sku?: string;
      q?: string;
      scope?: string;
      marketplace?: MarketplaceCode;
      severity?: QualitySeverity;
      category?: string;
      evidenceKind?: string;
      limit?: number;
      cursor?: string;
    } = {},
  ) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== "") search.set(key, String(value));
    });
    return request<ContentQualityResponse>(
      `/api/content-quality/issues${search.size ? `?${search}` : ""}`,
    );
  },
  confirmIssueAction: (issueId: string) =>
    request<NonNullable<ContentQualityIssue["followup"]>>(
      `/api/content-quality/issues/${encodeURIComponent(issueId)}/confirm-action`,
      { method: "POST" },
    ),
  scanContentQuality: (payload: {
    marketplaces?: MarketplaceCode[];
    sellerSkus?: string[];
    limit?: number;
  } = {}) =>
    request<{
      jobId: string;
      status: string;
      reused?: boolean;
      message?: string;
    }>("/api/content-quality/scan", {
      method: "POST",
      body: json(payload),
    }),
  contentQualityPlaybook: () =>
    request<PlaybookCatalogResponse>("/api/content-quality/playbook"),
  previewAttributeFix: (payload: {
    sellerSku: string;
    marketplace: MarketplaceCode;
    attributes: Record<string, unknown>;
  }) =>
    request<ListingPreview>("/api/content-quality/attribute-fix/preview", {
      method: "POST",
      body: json(payload),
    }),
  attributeFixContext: (issueId: string) =>
    request<AttributeFixContext>(
      `/api/content-quality/issues/${encodeURIComponent(issueId)}/attribute-fix-context`,
    ),
  brandLabCapabilities: () =>
    request<BrandLabCapabilities>("/api/brand-lab/capabilities"),

  settings: () => request<AppSettings>("/api/settings"),
  updateSettings: (settings: AppSettings) =>
    request<AppSettings>("/api/settings", { method: "PUT", body: json(settings) }),
  templates: async (): Promise<SettingsTemplate[]> => {
    const result = await request<SettingsTemplate[] | { items: SettingsTemplate[] }>(
      "/api/settings/templates",
    );
    return Array.isArray(result) ? result : result.items;
  },
  createTemplate: (template: Omit<SettingsTemplate, "id">) =>
    request<SettingsTemplate>("/api/settings/templates", {
      method: "POST",
      body: json(template),
    }),
  updateTemplate: (templateId: string, template: Partial<SettingsTemplate>) =>
    request<SettingsTemplate>(`/api/settings/templates/${encodeURIComponent(templateId)}`, {
      method: "PUT",
      body: json(template),
    }),
  deleteTemplate: (templateId: string) =>
    request<void>(`/api/settings/templates/${encodeURIComponent(templateId)}`, {
      method: "DELETE",
    }),
  shippingTemplates: async (marketplace: MarketplaceCode): Promise<ShippingTemplate[]> => {
    const result = await request<ShippingTemplate[] | { items: ShippingTemplate[] }>(
      `/api/shipping-templates?marketplace=${marketplace}`,
    );
    return Array.isArray(result) ? result : result.items;
  },
  shippingSettings: () => request<ShippingSettings>("/api/shipping-settings"),
  updateShippingSettings: (defaultTemplates: Partial<Record<MarketplaceCode, string>>) =>
    request<ShippingSettings>("/api/shipping-settings", {
      method: "PUT",
      body: json({ defaultTemplates }),
    }),

  financeOverview: (params: { days?: number; marketplace?: string } = {}) => {
    const search = new URLSearchParams();
    if (params.days) search.set("days", String(params.days));
    if (params.marketplace) search.set("marketplace", params.marketplace);
    return request<FinanceOverview>(`/api/finance/overview${search.size ? `?${search}` : ""}`);
  },
  financeEvents: (params: {
    days?: number;
    marketplace?: string;
    sellerSku?: string;
    type?: string;
    q?: string;
    limit?: number;
    offset?: number;
  } = {}) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== "") search.set(key, String(value));
    });
    return request<FinanceEventsResponse>(
      `/api/finance/events${search.size ? `?${search}` : ""}`,
    );
  },
  financeEventsExportUrl: (params: {
    days?: number;
    marketplace?: string;
    sellerSku?: string;
    type?: string;
    q?: string;
  } = {}) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== "") search.set(key, String(value));
    });
    return `/api/finance/events/export${search.size ? `?${search}` : ""}`;
  },
  financeSkus: (params: {
    days?: number;
    marketplace?: string;
    q?: string;
    sort?: string;
  } = {}) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== "") search.set(key, String(value));
    });
    return request<SkuProfitResponse>(`/api/finance/skus${search.size ? `?${search}` : ""}`);
  },
  financeCosts: async (): Promise<SkuCostEntry[]> => {
    const result = await request<SkuCostEntry[] | { items: SkuCostEntry[] }>("/api/finance/costs");
    return Array.isArray(result) ? result : result.items;
  },
  saveFinanceCost: (
    sellerSku: string,
    payload: {
      purchaseCostNet?: number | null;
      packagingCost?: number;
      shippingCost?: number;
      otherCost?: number;
      note?: string;
    },
  ) =>
    request<SkuCostEntry>(`/api/finance/costs/${encodeURIComponent(sellerSku)}`, {
      method: "PUT",
      body: json(payload),
    }),
  financeProfitPreview: (payload: {
    sellerSku: string;
    marketplace: string;
    price?: number | null;
    targetMarginPercent?: number | null;
  }) =>
    request<ProfitPreviewResult>("/api/finance/profit-preview", {
      method: "POST",
      body: json(payload),
    }),
  refreshFinanceFees: (payload: { sellerSkus: string[]; marketplaces?: string[] }) =>
    request<{
      items: FeeEstimateEntry[];
      skipped: Array<{ sellerSku: string; marketplace: string; reason: string }>;
    }>("/api/finance/fees/refresh", {
      method: "POST",
      body: json(payload),
    }),
  financeAlerts: (days?: number) =>
    request<FinanceAlertsResponse>(`/api/finance/alerts${days ? `?days=${days}` : ""}`),
  startFinanceSync: (payload: { lookbackDays?: number; refreshFeeEstimates?: boolean } = {}) =>
    request<{ jobId: string; status: string; reused?: boolean; message?: string }>(
      "/api/finance/sync",
      { method: "POST", body: json(payload) },
    ),

  sync: (marketplaces?: MarketplaceCode[]) =>
    request<{ jobId: string; status: string }>("/api/sync", {
      method: "POST",
      body: json({ marketplaces }),
    }),
  jobs: async (): Promise<Job[]> => {
    const result = await request<Job[] | PagedResponse<Job>>("/api/jobs");
    return Array.isArray(result) ? result : result.items;
  },
  job: (jobId: string) => request<Job>(`/api/jobs/${encodeURIComponent(jobId)}`),
  audit: async (): Promise<AuditEvent[]> => {
    const result = await request<AuditEvent[] | PagedResponse<AuditEvent>>("/api/audit");
    return Array.isArray(result) ? result : result.items;
  },
};

export function getApiError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Unbekannter Fehler. Bitte erneut versuchen.";
}
